import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { simulatorTabs as api } from "@/lib/api/simulator-tabs";
import type { SimulatorTabInput } from "@/lib/api/simulator-tabs";
import type { SimulatorTab } from "@/types/api";
import { useAuth } from "./useAuth";

export type SimulatorTabsStatus = "idle" | "loading" | "ready" | "error";

export interface SimulatorTabsContextValue {
  status: SimulatorTabsStatus;
  tabs: readonly SimulatorTab[];
  activeTabId: string | null;
  error: Error | null;
  reload: () => Promise<void>;
  setActiveTabId: (id: string | null) => void;
  open: (input: SimulatorTabInput) => Promise<SimulatorTab>;
  update: (
    id: string,
    patch: Partial<SimulatorTabInput>,
  ) => Promise<SimulatorTab>;
  close: (id: string) => Promise<void>;
  reorder: (ids: string[]) => Promise<void>;
  batchCreate: (
    inputs: SimulatorTabInput[],
  ) => Promise<readonly SimulatorTab[]>;
  batchUpdate: (
    updates: ({ id: string } & Partial<SimulatorTabInput>)[],
  ) => Promise<readonly SimulatorTab[]>;
  batchDestroy: (ids: string[]) => Promise<void>;
}

const SimulatorTabsContext = createContext<SimulatorTabsContextValue>(null!);

export function useSimulatorTabs(): SimulatorTabsContextValue {
  return useContext(SimulatorTabsContext);
}

function useSimulatorTabsProvider(): SimulatorTabsContextValue {
  const { status: authStatus } = useAuth();
  const [status, setStatus] = useState<SimulatorTabsStatus>("idle");
  const [tabs, setTabs] = useState<readonly SimulatorTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(
    () =>
      api
        .listSimulatorTabs()
        .then(({ simulator_tabs }) => {
          setTabs(simulator_tabs);
          setStatus("ready");
          setError(null);
          // Keep the active selection if it's still around; otherwise pick
          // the rightmost tab (legacy behavior — newest = end of bar).
          setActiveTabId((prev) => {
            if (prev && simulator_tabs.some((t) => t.id === prev)) return prev;
            return simulator_tabs.at(-1)?.id ?? null;
          });
        })
        .catch((e: Error) => {
          setError(e);
          setStatus("error");
        }),
    [],
  );

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    void reload();
  }, [authStatus, reload]);

  const open = useCallback(
    async (input: SimulatorTabInput): Promise<SimulatorTab> => {
      const { simulator_tab } = await api.createSimulatorTab(input);
      setTabs((prev) =>
        [...prev, simulator_tab].sort((a, b) => a.position - b.position),
      );
      setActiveTabId(simulator_tab.id);
      return simulator_tab;
    },
    [],
  );

  const update = useCallback(
    async (
      id: string,
      patch: Partial<SimulatorTabInput>,
    ): Promise<SimulatorTab> => {
      // Optimistic apply, then fire-and-forget reconcile of server-only
      // fields. Do NOT replace the row with the server response — sliders
      // and picker selections that have moved between request fire and
      // response would snap back to the snapshot we sent. Mirrors the
      // legacy `useTemplates.updateTemplate` invariant.
      // `prev` is captured inside the updater closure (which React runs at
      // commit time) so the revert path reads the just-captured value, not
      // null-at-queue-time.
      let prev: SimulatorTab | null = null;
      setTabs((cur) => {
        prev = cur.find((t) => t.id === id) ?? null;
        if (!prev) return cur;
        return cur.map((t) => (t.id === id ? { ...t, ...patch } : t));
      });
      try {
        const { simulator_tab } = await api.updateSimulatorTab(id, patch);
        setTabs((cur) =>
          cur.map((t) =>
            t.id === id
              ? { ...t, last_active_at: simulator_tab.last_active_at }
              : t,
          ),
        );
        return simulator_tab;
      } catch (e) {
        setTabs((cur) => {
          if (!prev) return cur;
          const restored = prev;
          return cur.map((t) => (t.id === id ? restored : t));
        });
        throw e;
      }
    },
    [],
  );

  const close = useCallback(
    async (id: string) => {
      await api.deleteSimulatorTab(id);
      setTabs((prev) => prev.filter((t) => t.id !== id));
      setActiveTabId((prev) => {
        if (prev !== id) return prev;
        const remaining = tabs.filter((t) => t.id !== id);
        return remaining.at(-1)?.id ?? null;
      });
    },
    [tabs],
  );

  const reorder = useCallback(async (ids: string[]) => {
    const { simulator_tabs } = await api.reorderSimulatorTabs(ids);
    setTabs(simulator_tabs);
  }, []);

  const batchCreate = useCallback(async (inputs: SimulatorTabInput[]) => {
    const { simulator_tabs } = await api.batchCreateSimulatorTabs(inputs);
    setTabs((prev) =>
      [...prev, ...simulator_tabs].sort((a, b) => a.position - b.position),
    );
    return simulator_tabs;
  }, []);

  const batchUpdate = useCallback(
    async (updates: ({ id: string } & Partial<SimulatorTabInput>)[]) => {
      // Optimistic per-id, same rationale as `update` above. The server
      // returns the full ordered list, but we only adopt server-only
      // fields (last_active_at) so concurrent edits aren't clobbered.
      const patchById = new Map(updates.map((u) => [u.id, u]));
      let prev: readonly SimulatorTab[] | null = null;
      setTabs((cur) => {
        prev = cur;
        return cur.map((t) => {
          const u = patchById.get(t.id);
          if (!u) return t;
          const { id: _id, ...patch } = u;
          return { ...t, ...patch };
        });
      });
      try {
        const { simulator_tabs } = await api.batchUpdateSimulatorTabs(updates);
        const serverById = new Map(simulator_tabs.map((s) => [s.id, s]));
        setTabs((cur) =>
          cur.map((t) => {
            const s = serverById.get(t.id);
            return s ? { ...t, last_active_at: s.last_active_at } : t;
          }),
        );
        return simulator_tabs;
      } catch (e) {
        setTabs((cur) => prev ?? cur);
        throw e;
      }
    },
    [],
  );

  const batchDestroy = useCallback(
    async (ids: string[]) => {
      await api.batchDestroySimulatorTabs(ids);
      const idSet = new Set(ids);
      setTabs((prev) => prev.filter((t) => !idSet.has(t.id)));
      setActiveTabId((prev) => {
        if (!prev || !idSet.has(prev)) return prev;
        const remaining = tabs.filter((t) => !idSet.has(t.id));
        return remaining.at(-1)?.id ?? null;
      });
    },
    [tabs],
  );

  return useMemo(
    () => ({
      status,
      tabs,
      activeTabId,
      error,
      reload,
      setActiveTabId,
      open,
      update,
      close,
      reorder,
      batchCreate,
      batchUpdate,
      batchDestroy,
    }),
    [
      status,
      tabs,
      activeTabId,
      error,
      reload,
      open,
      update,
      close,
      reorder,
      batchCreate,
      batchUpdate,
      batchDestroy,
    ],
  );
}

export function SimulatorTabsProvider({ children }: { children: ReactNode }) {
  const value = useSimulatorTabsProvider();
  return (
    <SimulatorTabsContext.Provider value={value}>
      {children}
    </SimulatorTabsContext.Provider>
  );
}
